import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useFilteredVendorDiscovery } from '@/hooks/useFilteredVendorDiscovery';
import { useCertificationTypes } from '@/hooks/useCertificationTypes';
import { useReviews } from '@/hooks/useReviews';
import { getVendorDisplayName } from '@/utils/vendorHelpers';
import { Search, Building, MapPin, Star, Shield, Users, Filter, X, Award, Wrench, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { analytics, ANALYTICS_EVENTS } from '@/lib/analytics';
import { VendorDetailSlideOver } from '@/components/vendor/VendorDetailSlideOver';
import { Review } from '@/lib/types/review';
interface VendorSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVendorsSelected: (vendors: any[]) => void;
  selectedVendors: any[];
  rfqCategory?: string;
  deliveryLocation?: { latitude: number; longitude: number };
}

interface SearchFilters {
  searchQuery?: string;
  location?: string;
  
  verification_level?: string;
  min_rating?: number;
  min_completed_jobs?: number;
  is_verified?: boolean;
  radius?: number;
  required_certifications?: string[];
}

export function VendorSelectionModal({ 
  isOpen, 
  onClose, 
  onVendorsSelected, 
  selectedVendors,
  rfqCategory,
  deliveryLocation 
}: VendorSelectionModalProps) {
  const { vendors, loading, searchVendors, getFeaturedVendors, clearResults, businessRulesActive } = useFilteredVendorDiscovery();
  const { types: certificationTypes, isLoading: certificationsLoading } = useCertificationTypes();
  const { getUserReviewAggregate, getUserReviews } = useReviews();
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<SearchFilters>({});
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCertifications, setSelectedCertifications] = useState<string[]>([]);
  const [localSelectedVendors, setLocalSelectedVendors] = useState<any[]>(selectedVendors);
  const [detailVendor, setDetailVendor] = useState<any>(null);
  const [detailVendorReviews, setDetailVendorReviews] = useState<Review[]>([]);
  const [detailVendorAggregate, setDetailVendorAggregate] = useState<any>(null);

  useEffect(() => {
    if (isOpen) {
      getFeaturedVendors(deliveryLocation);
      setLocalSelectedVendors(selectedVendors);
    }
  }, [isOpen, selectedVendors]);

  // Fetch reviews when detail vendor changes - use stable primitive dependency
  const detailVendorUserId = detailVendor?.user_id;
  useEffect(() => {
    let isCancelled = false;
    const fetchReviewData = async () => {
      if (detailVendorUserId) {
        const [aggregate, reviews] = await Promise.all([
          getUserReviewAggregate(detailVendorUserId),
          getUserReviews(detailVendorUserId)
        ]);
        if (!isCancelled) {
          setDetailVendorAggregate(aggregate);
          setDetailVendorReviews(reviews);
        }
      } else {
        setDetailVendorAggregate(null);
        setDetailVendorReviews([]);
      }
    };
    fetchReviewData();
    return () => { isCancelled = true; };
  }, [detailVendorUserId, getUserReviewAggregate, getUserReviews]);

  const handleSearch = async () => {
    // Collapse filters when search is triggered
    setShowFilters(false);
    
    if (!searchQuery.trim() && Object.keys(filters).length === 0 && selectedCertifications.length === 0) {
      getFeaturedVendors();
      return;
    }

    const searchFilters: SearchFilters = {
      ...filters,
      ...(searchQuery && { searchQuery: searchQuery }),
      ...(selectedCertifications.length > 0 && { required_certifications: selectedCertifications })
    };

    await searchVendors(searchFilters, deliveryLocation);
    
    // Track search performed
    analytics.track({
      name: ANALYTICS_EVENTS.SEARCH_PERFORMED,
      category: 'engagement',
      properties: {
        search_type: 'vendor',
        search_query: searchQuery,
        has_filters: Object.keys(filters).length > 0 || selectedCertifications.length > 0,
      },
    });
  };

  const handleFilterChange = (key: keyof SearchFilters, value: any) => {
    if (value === 'any') {
      setFilters(prev => {
        const newFilters = { ...prev };
        delete newFilters[key];
        return newFilters;
      });
    } else {
      setFilters(prev => ({
        ...prev,
        [key]: value
      }));
    }
  };

  const clearAllFilters = () => {
    setFilters({});
    setSearchQuery('');
    setSelectedCertifications([]);
    clearResults();
    getFeaturedVendors(deliveryLocation);
  };

  const handleVendorToggle = (vendor: any, checked: boolean) => {
    if (checked) {
      setLocalSelectedVendors(prev => [...prev, vendor]);
    } else {
      setLocalSelectedVendors(prev => prev.filter(v => v.id !== vendor.id));
    }
  };

  const handleSelectAll = () => {
    const newSelections = vendors.filter(vendor => 
      !localSelectedVendors.some(selected => selected.id === vendor.id)
    );
    setLocalSelectedVendors(prev => [...prev, ...newSelections]);
  };

  const handleDeselectAll = () => {
    const vendorIds = vendors.map(v => v.id);
    setLocalSelectedVendors(prev => prev.filter(v => !vendorIds.includes(v.id)));
  };

  const handleSave = () => {
    onVendorsSelected(localSelectedVendors);
    onClose();
  };

  const isVendorSelected = (vendorId: string) => {
    return localSelectedVendors.some(v => v.id === vendorId);
  };

  const getVerificationBadge = (vendor: any) => {
    if (!vendor.is_verified) return null;
    
    const level = vendor.verification_level;
    const colors = {
      basic: 'bg-blue-500',
      enhanced: 'bg-green-500',
      premium: 'bg-purple-500'
    };
    
    return (
      <Badge 
        variant="secondary" 
        className={`${colors[level as keyof typeof colors] || 'bg-gray-500'} text-white text-xs`}
      >
        <Shield className="h-3 w-3 mr-1" />
        {level?.toUpperCase()}
      </Badge>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col" aria-describedby="vendor-select-desc">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Select Vendors for RFQ</span>
            <div className="flex items-center gap-2 text-sm font-normal">
              <Users className="h-4 w-4" />
              {localSelectedVendors.length} selected
            </div>
          </DialogTitle>
          <p className="sr-only" id="vendor-select-desc">
            Search and filter vendors, then select one or more to invite to your RFQ.
          </p>
        </DialogHeader>

        {/* Distance-based filtering has been deprecated - vendors are now matched by capabilities and certifications */}

        {businessRulesActive && (
          <Alert className="mx-6">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              Your organization's vendor eligibility rules are active. Only vendors meeting your minimum requirements are shown.
            </AlertDescription>
          </Alert>
        )}

        <div className="flex-1 overflow-hidden flex flex-col space-y-4">
          {/* Search Section */}
          <Card>
            <CardContent className="p-4 space-y-4">
              <div className="flex gap-2">
                <div className="flex-1">
                  <Input
                    placeholder="Search by vendor name, keywords, or description..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  />
                </div>
                <Button onClick={() => setShowFilters(!showFilters)} variant="outline" size="sm">
                  <Filter className="h-4 w-4 mr-2" />
                  Filters
                </Button>
                <Button onClick={handleSearch} disabled={loading} size="sm">
                  {loading ? 'Searching...' : 'Search'}
                </Button>
              </div>

              {/* Advanced Filters */}
              {showFilters && (
                <div className="border-t pt-4 space-y-4">
                  {/* Row 1: Verification Level */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                    <div className="space-y-2">
                      <label className="text-sm font-medium">Verification Level</label>
                      <Select
                        value={filters.verification_level || 'any'}
                        onValueChange={(value) => handleFilterChange('verification_level', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Any verification" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">Any verification</SelectItem>
                          <SelectItem value="basic">Basic</SelectItem>
                          <SelectItem value="enhanced">Enhanced</SelectItem>
                          <SelectItem value="premium">Premium</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Row 2: Minimum Rating and Minimum Completed Jobs */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Minimum Rating</label>
                      <Input
                        type="number"
                        min="0"
                        max="5"
                        step="0.1"
                        placeholder="e.g., 4.0"
                        value={filters.min_rating || ''}
                        onChange={(e) => handleFilterChange('min_rating', e.target.value ? parseFloat(e.target.value) : undefined)}
                      />
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-medium">Minimum Completed Jobs</label>
                      <Input
                        type="number"
                        min="0"
                        placeholder="e.g., 10"
                        value={filters.min_completed_jobs || ''}
                        onChange={(e) => handleFilterChange('min_completed_jobs', e.target.value ? parseInt(e.target.value) : undefined)}
                      />
                    </div>
                  </div>

                  {/* Row 3: Required Certifications */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Required Certifications</label>
                    {certificationsLoading ? (
                      <div className="border rounded-md p-4">
                        <p className="text-sm text-muted-foreground">Loading certifications...</p>
                      </div>
                    ) : certificationTypes.length > 0 ? (
                      <>
                        <div className="border rounded-md p-4 space-y-2 max-h-48 overflow-y-auto">
                          {certificationTypes.map((cert) => (
                            <div key={cert.id} className="flex items-center space-x-2">
                              <Checkbox
                                id={`cert-${cert.id}`}
                                checked={selectedCertifications.includes(cert.id)}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedCertifications([...selectedCertifications, cert.id]);
                                  } else {
                                    setSelectedCertifications(selectedCertifications.filter(id => id !== cert.id));
                                  }
                                }}
                              />
                              <Label htmlFor={`cert-${cert.id}`} className="text-sm font-normal cursor-pointer">
                                {cert.name}
                              </Label>
                            </div>
                          ))}
                        </div>
                        {selectedCertifications.length > 0 && (
                          <p className="text-xs text-muted-foreground">
                            {selectedCertifications.length} certification{selectedCertifications.length !== 1 ? 's' : ''} selected
                          </p>
                        )}
                      </>
                    ) : (
                      <div className="border rounded-md p-4">
                        <p className="text-sm text-muted-foreground">No certifications available</p>
                      </div>
                    )}
                  </div>

                  {/* Distance Filter */}
                  {deliveryLocation && (
                    <div className="space-y-2">
                      <label className="text-sm font-medium flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        Maximum Distance
                      </label>
                      <Slider
                        value={[filters.radius ?? 500]}
                        onValueChange={(values) => handleFilterChange('radius', values[0] >= 500 ? undefined : values[0])}
                        min={25}
                        max={500}
                        step={25}
                        className="w-full"
                      />
                      <div className="flex justify-between text-sm text-muted-foreground">
                        <span>25 miles</span>
                        <span className="font-medium text-foreground">
                          {filters.radius ? `${filters.radius} miles` : 'Unlimited'}
                        </span>
                        <span>Unlimited</span>
                      </div>
                    </div>
                  )}

                  {!deliveryLocation && filters.radius && (
                    <Alert>
                      <AlertDescription>
                        Distance filtering requires a delivery location. Please add delivery details to your job.
                      </AlertDescription>
                    </Alert>
                  )}

                  <div className="flex gap-2">
                    <Button onClick={handleSearch} disabled={loading} size="sm">
                      Apply Filters
                    </Button>
                    <Button onClick={clearAllFilters} variant="outline" size="sm">
                      Clear All
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Selection Controls */}
          {vendors.length > 0 && (
            <div className="flex items-center justify-between">
              <div className="flex gap-2">
                <Button onClick={handleSelectAll} variant="outline" size="sm">
                  Select All
                </Button>
                <Button onClick={handleDeselectAll} variant="outline" size="sm">
                  Deselect All
                </Button>
              </div>
              <div className="text-sm text-muted-foreground">
                {vendors.length} vendors found
              </div>
            </div>
          )}

          {/* Vendor List */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[...Array(6)].map((_, i) => (
                  <Card key={i} className="animate-pulse">
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-4 h-4 bg-muted rounded"></div>
                        <div className="space-y-2 flex-1">
                          <div className="h-5 bg-muted rounded w-3/4"></div>
                          <div className="h-3 bg-muted rounded w-1/2"></div>
                          <div className="flex gap-2">
                            <div className="h-5 w-20 bg-muted rounded"></div>
                            <div className="h-5 w-20 bg-muted rounded"></div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : vendors.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {vendors.map((vendor) => (
                  <Card 
                    key={vendor.id} 
                    className={`cursor-pointer transition-colors ${
                      isVendorSelected(vendor.id) ? 'ring-2 ring-primary bg-primary/5' : 'hover:bg-muted/50'
                    }`}
                    onClick={() => setDetailVendor(vendor)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={isVendorSelected(vendor.id)}
                          onCheckedChange={(checked) => handleVendorToggle(vendor, checked as boolean)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className="flex-1 min-w-0 space-y-2">
                          {/* Header: Name + Business Type | Rating */}
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-1.5 flex-1 min-w-0">
                              <Building className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                              <h3 className="font-semibold text-base truncate">{getVendorDisplayName(vendor)}</h3>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {vendor.average_rating > 0 ? (
                                <div className="flex items-center gap-1">
                                  <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                                  <span className="text-sm font-semibold">{vendor.average_rating.toFixed(1)}</span>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1">
                                  <Star className="h-4 w-4 text-muted-foreground" />
                                  <span className="text-xs text-muted-foreground">No rating</span>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Consolidated Info Row: Location | Verification | Distance | Match */}
                          <div className="flex items-center gap-2 flex-wrap text-xs">
                            {vendor.country && (
                              <div className="flex items-center gap-1 text-muted-foreground">
                                <MapPin className="h-3 w-3" />
                                <span>
                                  {vendor.state && `${vendor.state}, `}
                                  {vendor.country}
                                </span>
                              </div>
                            )}
                            {vendor.country && getVerificationBadge(vendor) && (
                              <span className="text-muted-foreground">|</span>
                            )}
                            {getVerificationBadge(vendor)}
                          </div>

                          {/* Capabilities & Certifications */}
                          <div className="space-y-2">
                            {/* Capabilities */}
                            {vendor.vendor_capabilities && vendor.vendor_capabilities.length > 0 && (
                              <div className="space-y-1">
                                <div className="flex items-center gap-1">
                                  <Wrench className="h-3 w-3 text-muted-foreground" />
                                  <span className="text-xs font-medium text-muted-foreground">Capabilities</span>
                                </div>
                                <div className="flex flex-wrap gap-1">
                                  {vendor.vendor_capabilities.slice(0, 3).map((cap, index) => (
                                    <Badge key={index} variant="outline" className="text-xs px-2 py-0">
                                      {cap.capability?.name || 'Unknown'}{cap.business_percentage > 0 && ` (${cap.business_percentage}%)`}
                                    </Badge>
                                  ))}
                                  {vendor.vendor_capabilities.length > 3 && (
                                    <Badge variant="outline" className="text-xs px-2 py-0 text-muted-foreground">
                                      +{vendor.vendor_capabilities.length - 3} more
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            {/* Certifications */}
                            {vendor.vendor_certifications && vendor.vendor_certifications.length > 0 && (
                              <div className="space-y-1">
                                <div className="flex items-center gap-1">
                                  <Award className="h-3 w-3 text-muted-foreground" />
                                  <span className="text-xs font-medium text-muted-foreground">Certifications</span>
                                </div>
                                <div className="flex flex-wrap gap-1">
                                  {vendor.vendor_certifications
                                    .filter(cert => cert.verification_status === 'verified')
                                    .slice(0, 3)
                                    .map((cert, index) => (
                                      <Badge key={index} variant="secondary" className="text-xs px-2 py-0">
                                        {cert.certification_types?.name || cert.certification_name}
                                      </Badge>
                                    ))}
                                  {vendor.vendor_certifications.filter(cert => cert.verification_status === 'verified').length > 3 && (
                                    <Badge variant="secondary" className="text-xs px-2 py-0 text-muted-foreground">
                                      +{vendor.vendor_certifications.filter(cert => cert.verification_status === 'verified').length - 3} more
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            {/* Show message if no capabilities or certifications */}
                            {(!vendor.vendor_capabilities || vendor.vendor_capabilities.length === 0) && 
                             (!vendor.vendor_certifications || vendor.vendor_certifications.filter(c => c.verification_status === 'verified').length === 0) && (
                              <p className="text-xs text-muted-foreground italic">No capabilities or certifications listed</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="p-12 text-center">
                  <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No vendors found</h3>
                  <p className="text-muted-foreground mb-4">
                    Try adjusting your search criteria or browse featured vendors
                  </p>
                  <Button onClick={() => getFeaturedVendors(deliveryLocation)}>Browse Featured Vendors</Button>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Selected Vendors Summary */}
          {localSelectedVendors.length > 0 && (
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium">Selected Vendors ({localSelectedVendors.length})</h4>
                  <Button 
                    onClick={() => setLocalSelectedVendors([])} 
                    variant="ghost" 
                    size="sm"
                    className="text-muted-foreground"
                  >
                    Clear All
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {localSelectedVendors.map((vendor) => (
                    <Badge 
                      key={vendor.id} 
                      variant="secondary" 
                      className="flex items-center gap-1 px-2 py-1"
                    >
                      {getVendorDisplayName(vendor)}
                      <X 
                        className="h-3 w-3 cursor-pointer" 
                        onClick={() => handleVendorToggle(vendor, false)}
                      />
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave}
            disabled={localSelectedVendors.length === 0}
          >
            Select {localSelectedVendors.length} Vendor{localSelectedVendors.length !== 1 ? 's' : ''}
          </Button>
        </div>

        {/* Vendor Detail Slide-Over */}
        <VendorDetailSlideOver
          isOpen={!!detailVendor}
          onClose={() => setDetailVendor(null)}
          profile={detailVendor}
          certifications={detailVendor?.vendor_certifications?.filter((c: any) => c.verification_status === 'verified') || []}
          capabilities={detailVendor?.vendor_capabilities || []}
          reviewAggregate={detailVendorAggregate}
          reviews={detailVendorReviews}
          isSelected={detailVendor ? isVendorSelected(detailVendor.id) : false}
          onSelectVendor={() => {
            if (detailVendor) {
              const currentlySelected = isVendorSelected(detailVendor.id);
              handleVendorToggle(detailVendor, !currentlySelected);
            }
          }}
        />
      </DialogContent>
    </Dialog>
  );
}